import { isDefined, isNonEmpty } from '@sgme/fp';
import { type ChangeEvent, type FormEvent, useCallback, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { onClickSaveSharedTabUserList } from '@/store/sharedWorkspace/updateSharedUserList.workflow.ts';
import { onClickShareTab } from '@/store/sharedWorkspace/workflows/saveSharedTab.workflow.ts';
import {
  selectActiveTabSharedWithUsers,
  selectIsActiveTabShared,
  selectSharedTabOwner,
} from '@/store/tabs/selectors.ts';
import { onClickStopSharingTab } from '@/store/workspace/stopSharingTab.workflow.ts';

//////////
// MAIN //
//////////

export function ShareWorkspaceButton() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sharedTabOwner = useAppSelector(selectSharedTabOwner);
  const {
    currentUser: { login: userMail },
  } = useUserContext();

  const isDisabled = isDefined(sharedTabOwner) && sharedTabOwner !== userMail;

  const showModal = () => setIsModalOpen(true);
  const hideModal = () => setIsModalOpen(false);

  return (
    <>
      <Button
        variant="flat-primary"
        className="btn-lg btn-icon"
        onClick={showModal}
        disabled={isDisabled}
        data-e2e="share-tab-btn"
      >
        <i className="icon">share</i>
      </Button>
      {isModalOpen && <ShareWorkspaceModal hideModal={hideModal} />}
    </>
  );
}

///////////////
// SECONDARY //
///////////////

function ShareWorkspaceModal(props: { hideModal: () => void }) {
  const { hideModal } = props;

  const {
    currentUser: { login: ownerEmail },
  } = useUserContext();
  const dispatch = useAppDispatch();
  const isTabShared = useAppSelector(selectIsActiveTabShared);
  const sharedWithUsers = useAppSelector(selectActiveTabSharedWithUsers);
  const [users, setUsers] = useState<Set<string>>(new Set(sharedWithUsers));

  const displayUnshareButton = isTabShared && users.size === 0;
  const displayShareButton = !isTabShared;
  const displaySaveButton = isTabShared && users.size > 0;

  const addUser = useCallback((newUser: string) => {
    if (isNonEmpty(newUser)) {
      setUsers((prev) => new Set(prev.add(newUser)));
    }
  }, []);
  const removeUser = useCallback(
    (user: string) => () =>
      setUsers((prev) => {
        prev.delete(user);
        return new Set(prev);
      }),
    [],
  );

  const onClickShare = useCallback(() => {
    dispatch(onClickShareTab({ users: Array.from(users), ownerEmail }));
    hideModal();
  }, [hideModal, users, ownerEmail]);

  const onClickStopSharing = useCallback(() => {
    dispatch(onClickStopSharingTab());
    hideModal();
  }, [hideModal]);

  const onClickSave = useCallback(() => {
    dispatch(onClickSaveSharedTabUserList({ users: Array.from(users) }));
    hideModal();
  }, [hideModal, users]);

  return (
    <Modal show onHide={hideModal}>
      <ModalHeader closeButton>
        <ModalTitle>
          <FormattedMessage id="shared-workspace.modal.title" />
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <div className="mb-5 d-flex flex-wrap gap-3">
          {users.values().map((user) => (
            <div key={user} className="badge badge-default badge-dismissible">
              <button
                type="button"
                className="btn"
                onClick={removeUser(user)}
                data-e2e="share-tab-modal-remove-user-btn"
              >
                <em className="icon">close</em>
              </button>
              {user}
            </div>
          ))}
        </div>
        <AddUserInput onUserAdded={addUser} />
      </ModalBody>
      <ModalFooter>
        <Button variant="flat-primary" onClick={hideModal}>
          <FormattedMessage id="shared-workspace.modal.cancel-button" />
        </Button>
        {displayUnshareButton && (
          <Button onClick={onClickStopSharing} data-e2e="share-tab-modal-stop-sharing-btn">
            <FormattedMessage id="shared-workspace.modal.stop-sharing-button" />
          </Button>
        )}
        {displayShareButton && (
          <Button onClick={onClickShare} disabled={users.size === 0} data-e2e="share-tab-modal-share-btn">
            <FormattedMessage id="shared-workspace.modal.share-button" />
          </Button>
        )}
        {displaySaveButton && (
          <Button onClick={onClickSave}>
            <FormattedMessage id="shared-workspace.modal.save-button" />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

function AddUserInput(props: { onUserAdded: (newUser: string) => void }) {
  const { onUserAdded } = props;

  const [inputValue, setInputValue] = useState('');

  const onChange = (e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value);

  const onSubmitEmail = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    onUserAdded(inputValue);
    setInputValue('');
  };

  return (
    <form onSubmit={onSubmitEmail}>
      <div className="input-group">
        <Button type="submit" size="sm">
          <FormattedMessage id="shared-workspace.modal.add-user-button" />
          <i className="icon icon-sm ms-2">add</i>
        </Button>
        <input
          value={inputValue}
          onChange={onChange}
          className="form-control"
          type="email"
          placeholder="Email"
          data-e2e="share-tab-modal-email-input"
        />
      </div>
    </form>
  );
}
